import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Upload, 
  Trash2, 
  Plus, 
  Image as ImageIcon, 
  Loader2, 
  Box, 
  AlertCircle 
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { isImageFile, compressImage } from '../../utils/image.js';
import { uploadDataUrlToSupabaseStorage } from '../../services/storage-bucket.js';
import { updateInventoryItem, removeInventoryItem } from '../../state/store.js';
import { saveLocalData } from '../../state/storage.js';
import { syncAfterLocalChange } from '../../services/sync.js';
import { getAuthenticatedOperatorEmail } from '../../services/auth.js';
import { hasCloudSyncConfig } from '../../services/supabase.js';
import { rpcSaveInventoryItem } from '../../services/rpc.js';

export function ItemModal({ isOpen, itemId, onClose, onSaveSuccess }) {
  const inventory = useStore(s => s.inventory) || [];
  const settings = useStore(s => s.settings) || {};
  const fileInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [size, setSize] = useState('');
  const [unit, setUnit] = useState('ชิ้น');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageFileChanged, setImageFileChanged] = useState(false);

  // Lots state: array of { id, lot, quantity, expiryDate, boxContents }
  const [lots, setLots] = useState([
    { id: null, lot: '', quantity: 1, expiryDate: '', boxContents: [] }
  ]);

  // Load existing item data when editing
  useEffect(() => {
    if (!isOpen) return;

    if (itemId) {
      const activeItems = inventory.filter(i => !i.deleted);
      const targetItem = activeItems.find(i => i.id === itemId);
      if (targetItem) {
        setName(targetItem.name || '');
        setBrand(targetItem.brand || '');
        setSize(targetItem.size || '');
        setUnit(targetItem.unit || 'ชิ้น');
        setLocation(targetItem.location || '');
        setNotes(targetItem.notes || '');
        setImagePreview(targetItem.imageUrl || targetItem.imageBase64 || targetItem.image || '');
        setImageFileChanged(false);

        // Find all sibling lots for this product
        const cleanName = (targetItem.name || '').trim().toLowerCase();
        const cleanBrand = (targetItem.brand || '').trim().toLowerCase();
        const cleanSize = (targetItem.size || '').trim().toLowerCase();
        const cleanUnit = (targetItem.unit || '').trim().toLowerCase();

        const siblingLots = activeItems.filter(i => 
          (i.name || '').trim().toLowerCase() === cleanName &&
          (i.brand || '').trim().toLowerCase() === cleanBrand &&
          (i.size || '').trim().toLowerCase() === cleanSize &&
          (i.unit || '').trim().toLowerCase() === cleanUnit
        );

        if (siblingLots.length > 0) {
          setLots(siblingLots.map(l => ({
            id: l.id,
            lot: l.lot || '',
            quantity: l.quantity || 0,
            expiryDate: l.expiryDate || '',
            boxContents: Array.isArray(l.boxContents) ? [...l.boxContents] : []
          })));
        } else {
          setLots([{
            id: targetItem.id,
            lot: targetItem.lot || '',
            quantity: targetItem.quantity || 0,
            expiryDate: targetItem.expiryDate || '',
            boxContents: Array.isArray(targetItem.boxContents) ? [...targetItem.boxContents] : []
          }]);
        }
      }
    } else {
      // Create new fresh item
      setName('');
      setBrand('');
      setSize('');
      setUnit('ชิ้น');
      setLocation(settings.storageLocations?.split(',')[0]?.trim() || 'ตู้ A');
      setNotes('');
      setImagePreview('');
      setImageFileChanged(false);
      setLots([{
        id: null,
        lot: '',
        quantity: 1,
        expiryDate: '',
        boxContents: []
      }]);
    }
    setErrorMsg('');
  }, [isOpen, itemId, inventory, settings]);

  if (!isOpen) return null;

  // Image Upload & Client Compression
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setErrorMsg('กรุณาเลือกไฟล์รูปภาพที่ถูกต้อง (JPEG, PNG, WebP)');
      return;
    }

    try {
      const compressedDataUrl = await compressImage(file);
      setImagePreview(compressedDataUrl);
      setImageFileChanged(true);
      setErrorMsg('');
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการประมวลผลรูปภาพ: ' + err.message);
    }
  };

  // Lot Management
  const handleAddLot = () => {
    setLots(prev => [
      ...prev,
      { id: null, lot: `ล็อต ${prev.length + 1}`, quantity: 1, expiryDate: '', boxContents: [] }
    ]);
  };

  const handleRemoveLot = (index) => {
    if (lots.length <= 1) return;
    setLots(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateLot = (index, field, value) => {
    setLots(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Sub-box contents inside a lot
  const handleAddBox = (lotIndex) => {
    setLots(prev => {
      const next = [...prev];
      const curBoxes = next[lotIndex].boxContents || [];
      next[lotIndex] = {
        ...next[lotIndex],
        boxContents: [...curBoxes, { label: `กล่อง ${curBoxes.length + 1}`, quantity: 1, unit: unit || 'ชิ้น' }]
      };
      return next;
    });
  };

  const handleRemoveBox = (lotIndex, boxIndex) => {
    setLots(prev => {
      const next = [...prev];
      const curBoxes = [...(next[lotIndex].boxContents || [])];
      curBoxes.splice(boxIndex, 1);
      next[lotIndex] = { ...next[lotIndex], boxContents: curBoxes };
      return next;
    });
  };

  const handleUpdateBox = (lotIndex, boxIndex, field, value) => {
    setLots(prev => {
      const next = [...prev];
      const curBoxes = [...(next[lotIndex].boxContents || [])];
      curBoxes[boxIndex] = { ...curBoxes[boxIndex], [field]: value };
      next[lotIndex] = { ...next[lotIndex], boxContents: curBoxes };
      return next;
    });
  };

  // Submit & Save
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMsg('กรุณาระบุชื่อสินค้า / พัสดุ');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      let finalImageUrl = imagePreview;

      // If user uploaded a new base64 image, try uploading to Supabase Storage
      if (imageFileChanged && imagePreview && imagePreview.startsWith('data:')) {
        if (hasCloudSyncConfig()) {
          try {
            const fileName = `item_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.jpg`;
            finalImageUrl = await uploadDataUrlToSupabaseStorage(imagePreview, fileName);
          } catch (uploadErr) {
            console.warn('Storage upload note, saving locally:', uploadErr);
          }
        }
      }

      const operatorEmail = getAuthenticatedOperatorEmail() || 'user';
      const nowIso = new Date().toISOString();

      // Process each lot
      for (let i = 0; i < lots.length; i++) {
        const lotData = lots[i];
        const lotId = lotData.id || `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

        const itemPayload = {
          id: lotId,
          name: name.trim(),
          brand: brand.trim(),
          size: size.trim(),
          unit: unit.trim() || 'ชิ้น',
          location: location.trim(),
          notes: notes.trim(),
          lot: lotData.lot.trim() || 'ล็อตหลัก',
          quantity: Number(lotData.quantity) || 0,
          expiryDate: lotData.expiryDate || '',
          boxContents: lotData.boxContents || [],
          image: finalImageUrl || '',
          imageUrl: finalImageUrl || '',
          deleted: false,
          updatedAt: nowIso,
          updatedBy: operatorEmail
        };

        if (lotData.id) {
          updateInventoryItem(itemPayload);
        } else {
          itemPayload.createdBy = operatorEmail;
          updateInventoryItem(itemPayload);
        }

        // Online RPC save attempt
        if (hasCloudSyncConfig()) {
          try {
            await rpcSaveInventoryItem(itemPayload);
          } catch (rpcErr) {
            console.warn('RPC save item fallback to sync engine:', rpcErr);
          }
        }
      }

      saveLocalData();
      syncAfterLocalChange('item-mutation');

      onClose();
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึก: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const storageOptions = (settings.storageLocations || 'ตู้ A, ตู้ B, ตู้ C, ชั้นวาง 1, ชั้นวาง 2')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150 my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
              <Box className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-slate-900">
              {itemId ? 'แก้ไขข้อมูลพัสดุและล็อต' : 'เพิ่มพัสดุใหม่เข้าคลัง'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Image & Main Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Image Upload Box */}
            <div className="sm:col-span-1 flex flex-col items-center">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-36 rounded-xl border-2 border-dashed border-slate-300 hover:border-indigo-400 bg-slate-50 hover:bg-indigo-50/30 flex flex-col items-center justify-center p-2 text-center cursor-pointer transition-colors relative overflow-hidden group"
              >
                {imagePreview ? (
                  <>
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-semibold transition-opacity">
                      เปลี่ยนรูป
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center text-slate-400 gap-1">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-[11px] font-medium text-slate-500">คลิกอัปโหลดรูปภาพ</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              {imagePreview && (
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview('');
                    setImageFileChanged(true);
                  }}
                  className="mt-1 text-xs text-red-500 hover:text-red-700 cursor-pointer"
                >
                  ลบรูปภาพ
                </button>
              )}
            </div>

            {/* Product Basic Fields */}
            <div className="sm:col-span-2 space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  ชื่อสินค้า / พัสดุ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="เช่น สายน้ำเกลือ, ถุงมือยางไนไตรล์"
                  className="w-full px-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">ยี่ห้อ (Brand)</label>
                  <input
                    type="text"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="เช่น 3M, Nipro"
                    className="w-full px-3 py-1.5 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">ขนาด / สเปค</label>
                  <input
                    type="text"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    placeholder="เช่น Size M, 500ml"
                    className="w-full px-3 py-1.5 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Unit, Location, Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">หน่วยนับ</label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="เช่น ชิ้น, กล่อง, แผง, ขวด"
                className="w-full px-3 py-1.5 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">สถานที่จัดเก็บ / ตู้</label>
              <input
                type="text"
                list="locationList"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="เลือกหรือระบุตู้จัดเก็บ"
                className="w-full px-3 py-1.5 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
              <datalist id="locationList">
                {storageOptions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-800 mb-1">หมายเหตุเพิ่มเติม</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="ระบุข้อกำหนดเฉพาะ วิธีเก็บรักษา ฯลฯ"
              className="w-full px-3 py-1.5 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          {/* Lots Management Section */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                รายการล็อตสินค้า ({lots.length} ล็อต)
              </span>
              <button
                type="button"
                onClick={handleAddLot}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>เพิ่มล็อตย่อย</span>
              </button>
            </div>

            <div className="space-y-3">
              {lots.map((lotItem, idx) => (
                <div key={idx} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700">ล็อตที่ {idx + 1}</span>
                    {lots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLot(idx)}
                        className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                        title="ลบล็อตนี้"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-0.5">ชื่อ/รหัสล็อต</label>
                      <input
                        type="text"
                        value={lotItem.lot}
                        onChange={(e) => handleUpdateLot(idx, 'lot', e.target.value)}
                        placeholder="เช่น ล็อต 1 หรือ EXP2026"
                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-0.5">จำนวนคงเหลือ</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        required
                        value={lotItem.quantity}
                        onChange={(e) => handleUpdateLot(idx, 'quantity', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-0.5">วันหมดอายุ (Expiry)</label>
                      <input
                        type="date"
                        value={lotItem.expiryDate}
                        onChange={(e) => handleUpdateLot(idx, 'expiryDate', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white"
                      />
                    </div>
                  </div>

                  {/* Sub-box contents inside lot */}
                  <div className="pt-2 border-t border-slate-200/60">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-semibold text-slate-500">แจกแจงกล่องย่อย / ซองบรรจุ</span>
                      <button
                        type="button"
                        onClick={() => handleAddBox(idx)}
                        className="text-[11px] text-cyan-700 hover:text-cyan-900 font-semibold cursor-pointer"
                      >
                        + เพิ่มกล่องย่อย
                      </button>
                    </div>

                    {(lotItem.boxContents || []).map((box, bIdx) => (
                      <div key={bIdx} className="flex items-center gap-2 mb-1.5">
                        <input
                          type="text"
                          value={box.label}
                          onChange={(e) => handleUpdateBox(idx, bIdx, 'label', e.target.value)}
                          placeholder="ชื่อกล่อง เช่น กล่อง 1"
                          className="flex-1 px-2 py-1 text-xs rounded border border-slate-200 bg-white"
                        />
                        <input
                          type="number"
                          step="any"
                          min="0"
                          value={box.quantity}
                          onChange={(e) => handleUpdateBox(idx, bIdx, 'quantity', Number(e.target.value))}
                          placeholder="จำนวน"
                          className="w-20 px-2 py-1 text-xs rounded border border-slate-200 bg-white font-semibold"
                        />
                        <input
                          type="text"
                          value={box.unit}
                          onChange={(e) => handleUpdateBox(idx, bIdx, 'unit', e.target.value)}
                          placeholder="หน่วย"
                          className="w-16 px-2 py-1 text-xs rounded border border-slate-200 bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveBox(idx, bIdx)}
                          className="text-red-400 hover:text-red-600 p-1 cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs shadow-xs transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>{saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
